export type PublicArticleList = {
  /** ID */
  id?: number
  /** Slug */
  slug?: string
  /** Title */
  title?: string
  /** Lead */
  lead?: string
  /**
   * Data publikacji
   * @format date
   */
  publication_date?: string
}

export type PublicWidget = {
  /** ID */
  id?: number
  /**
   * X
   * @min 0
   * @max 2147483647
   */
  x?: number
  /**
   * Y
   * @min 0
   * @max 2147483647
   */
  y?: number
  /**
   * W
   * @min 0
   * @max 2147483647
   */
  w?: number
  /**
   * H
   * @min 0
   * @max 2147483647
   */
  h?: number
  /**
   * I
   * @minLength 1
   * @maxLength 6
   */
  i?: string
  /**
   * Nazwa robocza
   * @maxLength 255
   */
  working_name?: string
  /** Is hidden */
  is_hidden?: boolean
  /** Is title hidden */
  is_title_hidden?: boolean
  /** Is global */
  is_global?: boolean
  /** Polymorphic ctype */
  polymorphic_ctype?: number
}

export type PublicArticle = {
  /** ID */
  id?: number
  /** Slug */
  slug?: string
  /**
   * Slug
   * @format slug
   * @maxLength 50
   * @pattern ^[-a-zA-Z0-9_]+$
   */
  slug_pl?: string
  /**
   * Slug
   * @format slug
   * @maxLength 50
   * @pattern ^[-a-zA-Z0-9_]+$
   */
  slug_en?: string
  /** Title */
  title?: string
  /** Lead */
  lead?: string
  widgets: PublicWidget[]
  /**
   * Data publikacji
   * @format date
   */
  publication_date?: string
  /** Site title */
  site_title?: string
  /** Meta description */
  meta_description?: string
  /** Og title */
  og_title?: string
  /** Og description */
  og_description?: string
  /** Og image */
  og_image?: number | null
}

export type PublicAppStatusVersion = {
  /**
   * Ios minimum required version
   * @minLength 1
   */
  ios_minimum_required_version?: string
  /**
   * Ios minimum recommended version
   * @minLength 1
   */
  ios_minimum_recommended_version?: string
  /**
   * Android minimum required version
   * @minLength 1
   */
  android_minimum_required_version?: string
  /**
   * Android minimum recommended version
   * @minLength 1
   */
  android_minimum_recommended_version?: string
}

export type PublicImage = {
  /** ID */
  id?: number
  /** Images */
  images?: string
  /** Alt */
  alt?: string
  /** Title */
  title?: string
  /**
   * File
   * @format uri
   */
  file?: string
  /** Height */
  height?: number
  /** Width */
  width?: number
  /** Ppoi tuple */
  ppoi_tuple?: string
}

export type PublicSiteSettings = {
  /** ID */
  id?: number
  menu_logo: PublicImage
  footer_logo: PublicImage
  /** Is health questionnaire required */
  is_health_questionnaire_required?: boolean
  /**
   * Spotify url
   * @format uri
   * @maxLength 200
   */
  spotify_url?: string
  /**
   * Facebook url
   * @format uri
   * @maxLength 200
   */
  facebook_url?: string
  /**
   * Instagram url
   * @format uri
   * @maxLength 200
   */
  instagram_url?: string
  /**
   * Youtube url
   * @format uri
   * @maxLength 200
   */
  youtube_url?: string
  /**
   * Twitter url
   * @format uri
   * @maxLength 200
   */
  twitter_url?: string
  /** Send event notifications */
  send_event_notifications?: boolean
  /** Show navigation */
  show_navigation?: boolean
}

export type PublicEventActivityType = {
  /** ID */
  id?: number
  /** Name */
  name?: string
  /**
   * Slug
   * @format slug
   * @minLength 1
   * @pattern ^[-a-zA-Z0-9_]+$
   */
  slug?: string
}

export type PublicEventPlace = {
  /** ID */
  id?: number
  /** Name */
  name?: string
  /**
   * Slug
   * @format slug
   * @minLength 1
   * @pattern ^[-a-zA-Z0-9_]+$
   */
  slug?: string
}

export type PublicEventDate = {
  /** ID */
  id?: number
  /**
   * Date
   * @format date-time
   */
  date: string
  /** Date to */
  date_to?: string
  place: PublicEventPlace
  /** Is cancelled */
  is_cancelled?: boolean
  /** Fixed date */
  fixed_date?: string
  /** Duration */
  duration?: string
}

export type PublicEventCategory = {
  /** ID */
  id?: number
  /** Name */
  name?: string
  /**
   * Slug
   * @format slug
   * @minLength 1
   * @pattern ^[-a-zA-Z0-9_]+$
   */
  slug?: string
}

export type PublicEventList = {
  /** ID */
  id?: number
  /** Slug */
  slug?: string
  image: PublicImage
  /** Title */
  title?: string
  /** Min date */
  min_date?: string
  /** Max date */
  max_date?: string
  activity_type: PublicEventActivityType
  dates: PublicEventDate[]
  categories: PublicEventCategory[]
  /** All dates cancelled */
  all_dates_cancelled?: boolean
  closest_date: PublicEventDate
}

export type PublicEventSimple = {
  /** ID */
  id?: number
  /** Title */
  title?: string
  /** Slug */
  slug?: string
  image: PublicImage
  /** All dates cancelled */
  all_dates_cancelled?: boolean
  activity_type: PublicEventActivityType
  /** @uniqueItems true */
  categories: number[]
}

export type PublicEventDateWithEvent = {
  /** ID */
  id?: number
  event: PublicEventSimple
  /**
   * Date
   * @format date-time
   */
  date: string
  /** Date to */
  date_to?: string
  /** Fixed date */
  fixed_date?: string
  /** Fixed date to */
  fixed_date_to?: string
  place: PublicEventPlace
  /** Is cancelled */
  is_cancelled?: boolean
  /** Is archived */
  is_archived?: string
  /** Duration */
  duration?: string
}

export type PublicEvent = {
  /** ID */
  id?: number
  /** Slug */
  slug?: string
  /**
   * Slug
   * @format slug
   * @maxLength 50
   * @pattern ^[-a-zA-Z0-9_]+$
   */
  slug_pl?: string
  /**
   * Slug
   * @format slug
   * @maxLength 50
   * @pattern ^[-a-zA-Z0-9_]+$
   */
  slug_en?: string
  image: PublicImage
  /** Title */
  title?: string
  widgets: PublicWidget[]
  dates: PublicEventDate[]
  /** Min date */
  min_date?: string
  /** Max date */
  max_date?: string
  /** All dates cancelled */
  all_dates_cancelled?: boolean
  categories: PublicEventCategory[]
  activity_type: PublicEventActivityType
  /** Spotify track */
  spotify_track?: string
  /** Site title */
  site_title?: string
  /** Meta description */
  meta_description?: string
  /** Og title */
  og_title?: string
  /** Og description */
  og_description?: string
  og_image: PublicImage
}

export type PublicLinkWithIcon = {
  /**
   * Anchor type
   * @minLength 1
   */
  anchor_type?: string
  /**
   * Anchor id
   * @minLength 1
   */
  anchor_id?: string
  /** Slug */
  slug?: string
  /** Url */
  url?: string
  /** Label */
  label?: string
  /**
   * Attachment file
   * @format uri
   */
  attachment_file?: string
  icon: PublicImage
}

export type PublicMenuItem = {
  /** ID */
  id?: number
  links: PublicLinkWithIcon[]
  /** Label */
  label?: string
}

export type PublicMainMenu = {
  /** ID */
  id?: number
  items: PublicMenuItem[]
}

export type PublicRegisterDevice = {
  /**
   * Id
   * @format uuid
   */
  id?: string
  /**
   * Push token urządzenia
   * @maxLength 256
   */
  push_token?: string
  /**
   * Language
   * @minLength 1
   * @maxLength 12
   */
  language?: string
}

export type PublicFlatPage = {
  /** ID */
  id?: number
  /** Slug */
  slug?: string
  /**
   * Slug
   * @format slug
   * @maxLength 50
   * @pattern ^[-a-zA-Z0-9_]+$
   */
  slug_pl?: string
  /**
   * Slug
   * @format slug
   * @maxLength 50
   * @pattern ^[-a-zA-Z0-9_]+$
   */
  slug_en?: string
  /** Title */
  title?: string
  widgets: PublicWidget[]
  image: PublicImage
  /** Meta description */
  meta_description?: string
  /** Site title */
  site_title?: string
  /** Og title */
  og_title?: string
  /** Og description */
  og_description?: string
  og_image: PublicImage
  /**
   * Zmodyfikowany
   * @format date-time
   */
  modified?: string
}

export type PublicPlace = {
  /** ID */
  id?: number
  /** Name */
  name?: string
  /**
   * Slug
   * @format slug
   * @minLength 1
   * @pattern ^[-a-zA-Z0-9_]+$
   */
  slug?: string
  /** Category */
  category: number
  /** Description */
  description?: string
  icon: PublicImage
}

export type PublicPlaceCategory = {
  /** ID */
  id?: number
  /** Name */
  name?: string
  /**
   * Color
   * @minLength 1
   * @maxLength 255
   */
  color: string
  icon: PublicImage
  /** Zoom level */
  zoom_level?: 1 | 2 | 3
}

export type PublicMap = {
  /** ID */
  id?: number
  /** Data */
  data?: object
  /** Show navigation */
  show_navigation?: string
}

export type PublicMapEvent = {
  /** ID */
  id?: number
  /** Title */
  title?: string
  image: PublicImage
  /** Slug */
  slug?: string
}

export type PublicMapEventDate = {
  /** ID */
  id?: number
  /**
   * Date
   * @format date-time
   */
  date: string
  event: PublicMapEvent
}

export type PublicPlaceDetails = {
  /** ID */
  id?: number
  /** Name */
  name?: string
  /**
   * Slug
   * @format slug
   * @minLength 1
   * @pattern ^[-a-zA-Z0-9_]+$
   */
  slug?: string
  /** Category */
  category: number
  /** Description */
  description?: string
  icon: PublicImage
  events: PublicMapEventDate[]
}

export type PublicGeneratePhoneCodeForLogin = {
  /**
   * Phone
   * @minLength 1
   */
  phone: string
  /**
   * Valid to
   * @format date-time
   */
  valid_to?: string
  /** First name */
  first_name?: string
}

export type PublicGeneratePhoneCodeForRegistration = {
  /**
   * Phone
   * @minLength 1
   */
  phone: string
  /**
   * Valid to
   * @format date-time
   */
  valid_to?: string
}

export type PublicSplashScreenVideo = {
  /**
   * Video
   * @format uri
   */
  video?: string | null
}

export type PublicSplashScreen = {
  /** ID */
  id?: number
  /** Is active */
  is_active?: boolean
  logo: PublicImage
  image: PublicImage
  partner_image: PublicImage
  video: PublicSplashScreenVideo
}

export type PublicTicket = {
  /**
   * Ticket id
   * @minLength 1
   * @maxLength 128
   */
  ticket_id: string
  /**
   * Date
   * @format date
   */
  date: string
  /** Ticket type */
  ticket_type:
    | 'FREE_PARK'
    | 'PARK'
    | 'PARK_GDYNIA_CITIZENS_CARD_FREE'
    | 'CONCERT'
    | 'THEATER'
    | 'COMPS_VIP'
    | 'COMPS_PARK'
    | 'COMPS_PARK_CONCERT'
  /** Zones */
  zones?: string
  /**
   * Owner name
   * @maxLength 255
   */
  owner_name?: string
  /** Active qr code id */
  active_qr_code_id?: string
  /**
   * Health questionnaire
   * @format uuid
   */
  health_questionnaire?: string | null
  /** Is health questionnaire required */
  is_health_questionnaire_required?: string
  /** Questionnaire registration opens at */
  questionnaire_registration_opens_at?: string
  /** Is archived */
  is_archived?: boolean
  /**
   * Is child
   * Bilet dla dziecka
   */
  is_child?: boolean
  /** Main zone */
  main_zone?: string
  /** Main zone label */
  main_zone_label?: string
  /**
   * Stan
   * @minLength 1
   */
  status?: string
  /** Valid to */
  valid_to?: string
  /** Valid from */
  valid_from?: string
}

export type PhoneLogin = {
  /**
   * Phone
   * @minLength 1
   */
  phone: string
  /**
   * Code
   * @minLength 1
   * @maxLength 6
   */
  code: string
}

export type PublicRegister = {
  /**
   * Numer telefonu
   * @minLength 1
   * @maxLength 128
   */
  phone: string
  /**
   * Imię i nazwisko
   * @minLength 1
   * @maxLength 512
   */
  full_name: string
  /**
   * Data urodzenia
   * @format date
   */
  birth_date: string
  /**
   * Ticket
   * @minLength 1
   */
  ticket: string
  /**
   * Code
   * @maxLength 6
   */
  code?: string
  /**
   * Device name
   * @minLength 1
   */
  device_name: string
}

import type { AxiosInstance, AxiosRequestConfig, ResponseType } from 'axios'
import axios from 'axios'

export type QueryParamsType = Record<string | number, any>

export interface FullRequestParams
  extends Omit<AxiosRequestConfig, 'data' | 'params' | 'url' | 'responseType'> {
  /** set parameter to `true` for call `securityWorker` for this request */
  secure?: boolean
  /** request path */
  path: string
  /** content type of request body */
  type?: ContentType
  /** query params */
  query?: QueryParamsType
  /** format of response (i.e. response.json() -> format: "json") */
  format?: ResponseType
  /** request body */
  body?: unknown
}

export type RequestParams = Omit<
  FullRequestParams,
  'body' | 'method' | 'query' | 'path'
>

export interface ApiConfig<SecurityDataType = unknown>
  extends Omit<AxiosRequestConfig, 'data' | 'cancelToken'> {
  securityWorker?: (
    securityData: SecurityDataType | null
  ) => Promise<AxiosRequestConfig | void> | AxiosRequestConfig | void
  secure?: boolean
  format?: ResponseType
}

export enum ContentType {
  Json = 'application/json',
  FormData = 'multipart/form-data',
  UrlEncoded = 'application/x-www-form-urlencoded',
  Text = 'text/plain'
}

export class HttpClient<SecurityDataType = unknown> {
  public instance: AxiosInstance
  private securityData: SecurityDataType | null = null
  private securityWorker?: ApiConfig<SecurityDataType>['securityWorker']
  private secure?: boolean
  private format?: ResponseType

  constructor({
    securityWorker,
    secure,
    format,
    ...axiosConfig
  }: ApiConfig<SecurityDataType> = {}) {
    this.instance = axios.create({
      ...axiosConfig,
      baseURL:
        axiosConfig.baseURL || 'https://backend.opener-park.panowie.pro:443'
    })
    this.secure = secure
    this.format = format
    this.securityWorker = securityWorker
  }

  public setSecurityData = (data: SecurityDataType | null) => {
    this.securityData = data
  }

  protected mergeRequestParams(
    params1: AxiosRequestConfig,
    params2?: AxiosRequestConfig
  ): AxiosRequestConfig {
    return {
      ...this.instance.defaults,
      ...params1,
      ...(params2 || {}),
      headers: {
        ...(params1.headers || {}),
        ...((params2 && params2.headers) || {})
      }
    }
  }

  protected stringifyFormItem(formItem: unknown) {
    if (typeof formItem === 'object' && formItem !== null) {
      return JSON.stringify(formItem)
    } else {
      return `${formItem}`
    }
  }

  protected createFormData(input: Record<string, unknown>): FormData {
    return Object.keys(input || {}).reduce((formData, key) => {
      const property = input[key]
      const propertyContent: any[] =
        property instanceof Array ? property : [property]

      for (const formItem of propertyContent) {
        const isFileType = formItem instanceof Blob || formItem instanceof File
        formData.append(
          key,
          isFileType ? formItem : this.stringifyFormItem(formItem)
        )
      }

      return formData
    }, new FormData())
  }

  public request = async <T = any, _E = any>({
    secure,
    path,
    type,
    query,
    format,
    body,
    ...params
  }: FullRequestParams): Promise<T> => {
    const secureParams =
      ((typeof secure === 'boolean' ? secure : this.secure) &&
        this.securityWorker &&
        (await this.securityWorker(this.securityData))) ||
      {}
    const requestParams = this.mergeRequestParams(params, secureParams)
    const responseFormat = format || this.format || undefined

    if (
      type === ContentType.FormData &&
      body &&
      body !== null &&
      typeof body === 'object'
    ) {
      body = this.createFormData(body as Record<string, unknown>)
    }

    if (
      type === ContentType.Text &&
      body &&
      body !== null &&
      typeof body !== 'string'
    ) {
      body = JSON.stringify(body)
    }

    return this.instance
      .request({
        ...requestParams,
        headers: {
          ...(requestParams.headers || {}),
          ...(type && type !== ContentType.FormData
            ? { 'Content-Type': type }
            : {})
        },
        params: query,
        responseType: responseFormat,
        data: body,
        url: path
      })
      .then(response => response.data)
  }
}

type Flatten<T> = T extends any[] ? T[number] : T
type ChangeTypeOfKeys<T extends object, Keys extends keyof T, NewType> = {
  [key in keyof T]: key extends Keys ? NewType : T[key]
}
type MapObjectKeysToTrue<ObjectType extends Record<PropertyKey, any>> = {
  [key in keyof ObjectType]?: ObjectType[key] extends Record<PropertyKey, any>
    ? true | MapObjectKeysToTrue<Flatten<ObjectType[key]>>
    : true
}
type FetchKeysType = { [key in PropertyKey]: true | FetchKeysType }
type FetchKeys<ResponseModel> = ResponseModel extends {
  count?: number
  results?: Array<infer DataModel>
}
  ? DataModel extends Record<PropertyKey, any>
    ? MapObjectKeysToTrue<DataModel>
    : DataModel
  : ResponseModel extends Record<PropertyKey, any>
  ? MapObjectKeysToTrue<Flatten<ResponseModel>>
  : ResponseModel

type NestedSafePick<ResponseModel, FetchKeysType> = ResponseModel extends Array<
  infer Model
>
  ? NestedSafePick<Model, FetchKeysType>[]
  : {
      [key in keyof FetchKeysType]: FetchKeysType[key] extends Record<
        PropertyKey,
        any
      >
        ? key extends keyof ResponseModel
          ? ResponseModel[key] extends Array<infer DataModel>
            ? NestedSafePick<DataModel, FetchKeysType[key]>[]
            : NestedSafePick<ResponseModel[key], FetchKeysType[key]>
          : never
        : key extends keyof ResponseModel
        ? ResponseModel[key] extends Array<infer DataModel>
          ? DataModel[]
          : ResponseModel[key]
        : never
    }

type PickKeys<ResponseModel, FetchKeysType> = {} extends FetchKeysType
  ? ResponseModel
  : ResponseModel extends {
      count?: number
      results?: Array<infer DataModel>
    }
  ? ChangeTypeOfKeys<
      ResponseModel,
      'results',
      NestedSafePick<DataModel, FetchKeysType>[]
    >
  : ResponseModel extends Array<infer DataModel>
  ? NestedSafePick<DataModel, FetchKeysType>[]
  : NestedSafePick<ResponseModel, FetchKeysType>

function postprocessQuery(query?: any) {
  if (!query) return query

  const parsedQuery = query
  if (query.fetchKeys) {
    function getStringifiedQueryKeys(keys: FetchKeysType) {
      let query = '{'
      Object.entries(keys).forEach(([key, value], index) => {
        if (typeof value === 'object') {
          const keyToAdd = `${key}${getStringifiedQueryKeys(value)}`
          query += index > 0 ? `,${keyToAdd}` : keyToAdd
        } else {
          query += index > 0 ? `,${key}` : key
        }
      })
      return query + '}'
    }

    parsedQuery.query = getStringifiedQueryKeys(query.fetchKeys)
  }

  delete parsedQuery.fetchKeys
  return parsedQuery
}

/**
 * @title API* @version v1* @baseUrl https://backend.opener-park.panowie.pro:443*  * API for frontend*/
export class Api<
  SecurityDataType extends unknown
> extends HttpClient<SecurityDataType> {
  public = {
    /**
     * No description
     *
     * @tags public
     * @name PublicArticlesList
     * @request GET:/public/articles/
     * @secure
     */
    publicArticlesList: <
      T extends FetchKeys<{
        count: number
        /** @format uri */
        next?: string | null
        /** @format uri */
        previous?: string | null
        results: PublicArticleList[]
      }>
    >(
      query?: {
        tags?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<
          {
            count: number
            /** @format uri */
            next?: string | null
            /** @format uri */
            previous?: string | null
            results: PublicArticleList[]
          },
          T
        >,
        any
      >({
        path: `/public/articles/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags public
     * @name PublicArticlesRead
     * @request GET:/public/articles/{slug}/
     * @secure
     */,
    publicArticlesRead: <T extends FetchKeys<PublicArticle>>(
      slug: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PublicArticle, T>, any>({
        path: `/public/articles/${slug}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags public
     * @name PublicCoreAllDataList
     * @request GET:/public/core/all-data/
     * @secure
     */,
    publicCoreAllDataList: <T extends FetchKeys<void>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/public/core/all-data/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags public
     * @name PublicCoreAppStatusRead
     * @request GET:/public/core/app-status/
     * @secure
     */,
    publicCoreAppStatusRead: <T extends FetchKeys<PublicAppStatusVersion>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PublicAppStatusVersion, T>, any>({
        path: `/public/core/app-status/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags public
     * @name PublicCoreHealthcheckList
     * @request GET:/public/core/healthcheck/
     * @secure
     */,
    publicCoreHealthcheckList: <T extends FetchKeys<void>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/public/core/healthcheck/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags public
     * @name PublicCoreSettingsRead
     * @request GET:/public/core/settings/
     * @secure
     */,
    publicCoreSettingsRead: <T extends FetchKeys<PublicSiteSettings>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PublicSiteSettings, T>, any>({
        path: `/public/core/settings/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags public
     * @name PublicEventsList
     * @request GET:/public/events/
     * @secure
     */,
    publicEventsList: <
      T extends FetchKeys<{
        count: number
        /** @format uri */
        next?: string | null
        /** @format uri */
        previous?: string | null
        results: PublicEventList[]
      }>
    >(
      query?: {
        id?: number
        date_from?: string
        date_to?: string
        search?: string
        activity_type?: string
        favourited_by?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<
          {
            count: number
            /** @format uri */
            next?: string | null
            /** @format uri */
            previous?: string | null
            results: PublicEventList[]
          },
          T
        >,
        any
      >({
        path: `/public/events/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags public
     * @name PublicEventsActivityTypesList
     * @request GET:/public/events/activity-types/
     * @secure
     */,
    publicEventsActivityTypesList: <
      T extends FetchKeys<{
        count: number
        /** @format uri */
        next?: string | null
        /** @format uri */
        previous?: string | null
        results: PublicEventActivityType[]
      }>
    >(
      query?: {
        categories?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<
          {
            count: number
            /** @format uri */
            next?: string | null
            /** @format uri */
            previous?: string | null
            results: PublicEventActivityType[]
          },
          T
        >,
        any
      >({
        path: `/public/events/activity-types/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags public
     * @name PublicEventsDatesList
     * @request GET:/public/events/dates/
     * @secure
     */,
    publicEventsDatesList: <
      T extends FetchKeys<{
        count: number
        /** @format uri */
        next?: string | null
        /** @format uri */
        previous?: string | null
        results: PublicEventDateWithEvent[]
      }>
    >(
      query?: {
        id?: number
        date_from?: string
        date_to?: string
        event?: string
        event_activity_type?: string
        search?: string
        archived?: string
        fixed_date?: string
        categories?: string
        /** Which field to use when ordering the results. */
        ordering?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<
          {
            count: number
            /** @format uri */
            next?: string | null
            /** @format uri */
            previous?: string | null
            results: PublicEventDateWithEvent[]
          },
          T
        >,
        any
      >({
        path: `/public/events/dates/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags public
     * @name PublicEventsDatesCalendarList
     * @request GET:/public/events/dates/calendar/
     * @secure
     */,
    publicEventsDatesCalendarList: <T extends FetchKeys<void>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/public/events/dates/calendar/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags public
     * @name PublicEventsRead
     * @request GET:/public/events/{slug}/
     * @secure
     */,
    publicEventsRead: <T extends FetchKeys<PublicEvent>>(
      slug: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PublicEvent, T>, any>({
        path: `/public/events/${slug}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags public
     * @name PublicMenuMainRead
     * @request GET:/public/menu/main/
     * @secure
     */,
    publicMenuMainRead: <T extends FetchKeys<PublicMainMenu>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PublicMainMenu, T>, any>({
        path: `/public/menu/main/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags public
     * @name PublicNotificationsRegisterDeviceCreate
     * @request POST:/public/notifications/register-device/
     * @secure
     */,
    publicNotificationsRegisterDeviceCreate: <
      T extends FetchKeys<PublicRegisterDevice>
    >(
      data: PublicRegisterDevice,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PublicRegisterDevice, T>, any>({
        path: `/public/notifications/register-device/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags public
     * @name PublicPagesList
     * @request GET:/public/pages/
     * @secure
     */,
    publicPagesList: <
      T extends FetchKeys<{
        count: number
        /** @format uri */
        next?: string | null
        /** @format uri */
        previous?: string | null
        results: PublicFlatPage[]
      }>
    >(
      query?: {
        /** A search term. */
        search?: string
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<
          {
            count: number
            /** @format uri */
            next?: string | null
            /** @format uri */
            previous?: string | null
            results: PublicFlatPage[]
          },
          T
        >,
        any
      >({
        path: `/public/pages/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags public
     * @name PublicPagesRead
     * @request GET:/public/pages/{slug}/
     * @secure
     */,
    publicPagesRead: <T extends FetchKeys<PublicFlatPage>>(
      slug: string,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PublicFlatPage, T>, any>({
        path: `/public/pages/${slug}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags public
     * @name PublicPlacesList
     * @request GET:/public/places/
     * @secure
     */,
    publicPlacesList: <
      T extends FetchKeys<{
        count: number
        /** @format uri */
        next?: string | null
        /** @format uri */
        previous?: string | null
        results: PublicPlace[]
      }>
    >(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<
          {
            count: number
            /** @format uri */
            next?: string | null
            /** @format uri */
            previous?: string | null
            results: PublicPlace[]
          },
          T
        >,
        any
      >({
        path: `/public/places/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags public
     * @name PublicPlacesCategoriesList
     * @request GET:/public/places/categories/
     * @secure
     */,
    publicPlacesCategoriesList: <
      T extends FetchKeys<{
        count: number
        /** @format uri */
        next?: string | null
        /** @format uri */
        previous?: string | null
        results: PublicPlaceCategory[]
      }>
    >(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<
          {
            count: number
            /** @format uri */
            next?: string | null
            /** @format uri */
            previous?: string | null
            results: PublicPlaceCategory[]
          },
          T
        >,
        any
      >({
        path: `/public/places/categories/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags public
     * @name PublicPlacesCategoriesRead
     * @request GET:/public/places/categories/{id}/
     * @secure
     */,
    publicPlacesCategoriesRead: <T extends FetchKeys<PublicPlaceCategory>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PublicPlaceCategory, T>, any>({
        path: `/public/places/categories/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags public
     * @name PublicPlacesMapList
     * @request GET:/public/places/map/
     * @secure
     */,
    publicPlacesMapList: <
      T extends FetchKeys<{
        count: number
        /** @format uri */
        next?: string | null
        /** @format uri */
        previous?: string | null
        results: PublicMap[]
      }>
    >(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<
        PickKeys<
          {
            count: number
            /** @format uri */
            next?: string | null
            /** @format uri */
            previous?: string | null
            results: PublicMap[]
          },
          T
        >,
        any
      >({
        path: `/public/places/map/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags public
     * @name PublicPlacesMapRead
     * @request GET:/public/places/map/{id}/
     * @secure
     */,
    publicPlacesMapRead: <T extends FetchKeys<PublicMap>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PublicMap, T>, any>({
        path: `/public/places/map/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags public
     * @name PublicPlacesRead
     * @request GET:/public/places/{id}/
     * @secure
     */,
    publicPlacesRead: <T extends FetchKeys<PublicPlaceDetails>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PublicPlaceDetails, T>, any>({
        path: `/public/places/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags public
     * @name PublicSmsAuthLoginSendCodeCreate
     * @request POST:/public/sms-auth/login/send-code/
     * @secure
     */,
    publicSmsAuthLoginSendCodeCreate: <
      T extends FetchKeys<PublicGeneratePhoneCodeForLogin>
    >(
      data: PublicGeneratePhoneCodeForLogin,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PublicGeneratePhoneCodeForLogin, T>, any>({
        path: `/public/sms-auth/login/send-code/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags public
     * @name PublicSmsAuthRegistrationSendCodeCreate
     * @request POST:/public/sms-auth/registration/send-code/
     * @secure
     */,
    publicSmsAuthRegistrationSendCodeCreate: <
      T extends FetchKeys<PublicGeneratePhoneCodeForRegistration>
    >(
      data: PublicGeneratePhoneCodeForRegistration,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PublicGeneratePhoneCodeForRegistration, T>, any>({
        path: `/public/sms-auth/registration/send-code/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags public
     * @name PublicSplashScreenSplashScreenRead
     * @request GET:/public/splash-screen/splash-screen/
     * @secure
     */,
    publicSplashScreenSplashScreenRead: <
      T extends FetchKeys<PublicSplashScreen>
    >(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PublicSplashScreen, T>, any>({
        path: `/public/splash-screen/splash-screen/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
    /**
     * @description view for ebilet purposes. Should be removed before prod deploy
     *
     * @tags public
     * @name PublicTicketsEbiletList
     * @request GET:/public/tickets/ebilet/
     * @secure
     */,
    publicTicketsEbiletList: <T extends FetchKeys<void>>(
      query?: {
        /** A page number within the paginated result set. */
        page?: number
        /** Number of results to return per page. */
        page_size?: number

        fetchKeys?: T
      },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/public/tickets/ebilet/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags public
     * @name PublicTicketsOwnerName
     * @request POST:/public/tickets/{ticket_id}/owner-name/
     * @secure
     */,
    publicTicketsOwnerName: <T extends FetchKeys<PublicTicket>>(
      ticketId: string,
      data: PublicTicket,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PublicTicket, T>, any>({
        path: `/public/tickets/${ticketId}/owner-name/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
    /**
     * No description
     *
     * @tags public
     * @name PublicUsersAuthLoginCreate
     * @request POST:/public/users/auth/login/
     * @secure
     */,
    publicUsersAuthLoginCreate: <T extends FetchKeys<PhoneLogin>>(
      data: PhoneLogin,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PhoneLogin, T>, any>({
        path: `/public/users/auth/login/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      }) /**
 * @description Accepts/Returns nothing.
* 
 * @tags public
 * @name PublicUsersAuthLogoutList
 * @summary Calls Django logout method and delete the Token object
assigned to the current User object.
 * @request GET:/public/users/auth/logout/
 * @secure
*/,
    publicUsersAuthLogoutList: <T extends FetchKeys<void>>(
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/public/users/auth/logout/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        ...params
      }) /**
 * @description Accepts/Returns nothing.
* 
 * @tags public
 * @name PublicUsersAuthLogoutCreate
 * @summary Calls Django logout method and delete the Token object
assigned to the current User object.
 * @request POST:/public/users/auth/logout/
 * @secure
*/,
    publicUsersAuthLogoutCreate: <T extends FetchKeys<void>>(
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<void, T>, any>({
        path: `/public/users/auth/logout/`,
        method: 'POST',
        secure: true,
        ...params
      })
    /**
     * No description
     *
     * @tags public
     * @name PublicUsersAuthRegisterCreate
     * @request POST:/public/users/auth/register/
     * @secure
     */,
    publicUsersAuthRegisterCreate: <T extends FetchKeys<PublicRegister>>(
      data: PublicRegister,
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PublicRegister, T>, any>({
        path: `/public/users/auth/register/`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params
      })
  }
  widgets = {
    /**
     * No description
     *
     * @tags widgets
     * @name WidgetsPublicRead
     * @request GET:/widgets/public/{id}/
     * @secure
     */
    widgetsPublicRead: <T extends FetchKeys<PublicWidget>>(
      id: number,
      query?: { fetchKeys?: T },
      params: RequestParams = {}
    ) =>
      this.request<PickKeys<PublicWidget, T>, any>({
        path: `/widgets/public/${id}/`,
        method: 'GET',
        query: postprocessQuery(query),
        secure: true,
        format: 'json',
        ...params
      })
  }
}
